/* Route.js */

import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Route, Redirect, useHistory } from 'react-router-dom';
import { useAuth } from '../context/auth/AuthContext';
import Maintenance from '../pages/maintenance/Maintenance';
import WAM from '../services/WAM';
import { toast } from 'react-toastify';
import Strings from '../translations/Translations';
import { Track as GA_Track } from '../helpers/GoogleAnalytics';

GA_Track();

export default function RouteWrapper({ component: Component, isPrivate, isAuth, notFound, ...rest }) {
  const { authenticated, setActiveLinkTo, logout } = useAuth();
  const history = useHistory();

  // Listening route change
  useEffect(() => {
    history.listen(() => {
      const hash = window.location.hash;
      const path = hash.split('?')[0];
      setActiveLinkTo(path);
    })
  }, [history, setActiveLinkTo]);

  // Processing on first time page is opened
  const validateSession = useCallback(async () => {
    if (authenticated) {
      const { un, al } = authenticated;
      if (un && al) {
        let doLogout = false;
        let errorMessage = Strings('error_while_validating_login');
        try {
          const response = await WAM.validateSession(un, al);
          if (response.result !== 'success') {
            doLogout = true;
            if (response.error) { errorMessage = response.error; }
          }
        } catch (err) {
          console.error(err);
          doLogout = true;
        }

        if (doLogout) {
          toast.error(errorMessage);
          logout(false);
        }
      }
    }
  }, [authenticated, logout]);
  useEffect(() => { validateSession(); }, [validateSession]);

  // Maintenance
  const htmlMaintenanceMode = document.APPLICATION_MAINTENANCE;
  const envMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE;
  if (htmlMaintenanceMode === true || envMaintenanceMode === 'true') {
    return (
      <Route
        {...rest}
        render={() => (
          <Maintenance />
        )}
      />
    )
  }

  if (notFound) {
    return (
      <Route
        {...rest}
        render={(props) => (
          <Component {...props} />
        )}
      />
    )
  }

  let signed = null;
  if (authenticated) {
    signed = true;
  } else {
    signed = false;
  }

  if (!signed && isPrivate) {
    return <Redirect to="/login" />;
  }
  if (signed && isAuth) {
    return <Redirect to="/dashboard" />;
  }

  return (
    <Route
      {...rest}
      render={(props) => (
        <Component {...props} />
      )}
    />
  );
}

RouteWrapper.propTypes = {
  isPrivate: PropTypes.bool,
  component: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
};

RouteWrapper.defaultProps = {
  isPrivate: false,
};
